import {defineStore} from "pinia";
import {reactive, ref, watch} from "vue";
import {serializeFormData} from "../helpers/CaseTransformer.js";
import {parsePartialCandidateData} from "../helpers/HandleStreamedJson.js";
import {computeCandidateTrustScore} from "../helpers/TrustScore.js";
import {usaCities} from "../constants/Lists.js";
import {useCandidateProfileStore} from "./CandidateProfileStore.js";
import {useAccountStore} from "./AccountStore.js";
import ParseResumeApi from "../api/ParseResumeApi.js";
import * as Sentry from "@sentry/vue";
import {useLocalStorage} from "@vueuse/core";
import {cloudfrontUrlToFile} from "../helpers/CloudfrontHelpers";
import {candidateStateName, locationFields, locationTuple, mergeResumeLocation, validCoordinatePair} from "../helpers/CandidateLocationMerge.js";

export const useParseResumeStore = defineStore("resumeParser", () => {
  const defaultResume = {
    uuid: null,
    locationExpectedRevision: null,
    locationExampleEvidence: {},
    file: null,
    fileMimeType: null,
    fileDataUrl: null,
    tosChecked: false,
    parsedContent: "",
    name: "",
    formattedSize: "",
    uploading: false,
    streaming: false,
    confirm: false,
    error: null,
    thinking: false,
    loading: false,
  };

  const defaultCandidate = {
    trustScore: 53,
    avatar: "",
    banner: "",
    bannerFile: {
      path: "",
      file: null,
    },
    profilePhoto: {
      path: "",
      file: null,
    },
    about: "",
    summary: {
      prompt: "",
      answer: "",
    },
    location: "",
    lat: null,
    lng: null,
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    willingToRelocate: "Open to Relocation",
    remote: "",
    interestedIn: [],
    industries: [],
    fullTime: ["Full Time"],
    availability: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    desiredHourlyPay: 15,
    compensationFlexibility: 0.2,
    prefersHourlyOverSalary: true,
    exceptionalAbility: [],
    profileVideo1: {
      path: "",
      prompt: "",
      file: null,
    },
    profileVideo2: {
      path: "",
      prompt: "",
      file: null,
    },
    skills: [],
    accomplishments: [],
    careerPassport: [],
    education: [],
    certificatesCertificationsAndLicenses: [],
    languages: ["English"],
    studentAthlete: false,
    hobbies: "",
    military: false,
    linkedIn: "",
    instagram: "",
    facebook: "",
    website: "",
  };

  const defaultParsedSearchTerms = {
    industries: [],
    jobs: [],
  };

  const resume = reactive(Object.assign({showModal: false}, defaultResume));
  const candidate = reactive(Object.assign({}, defaultCandidate));
  const parsedSearchTerms = reactive(Object.assign({}, defaultParsedSearchTerms));
  const localResume = useLocalStorage("localResume", {});
  let parseGeneration = 0;
  let activeEventSource = null;
  const locationManuallyEdited = ref(false);
  const invalidate = () => {
    parseGeneration++;
    activeEventSource?.close();
    activeEventSource = null;
    clearTimeout(thinkingTimout);
    resume.loading = resume.uploading = resume.streaming = resume.thinking = false;
  };
  const replace = (target, defaults) => {
    Object.keys(target).forEach(key => delete target[key]);
    Object.assign(target, JSON.parse(JSON.stringify(defaults)));
  };
  const clearState = () => {
    invalidate();
    replace(resume, {...defaultResume, showModal: false});
    replace(candidate, defaultCandidate);
    replace(parsedSearchTerms, defaultParsedSearchTerms);
    localResume.value = {};
    locationManuallyEdited.value = false;
  };
  watch(() => useAccountStore().user?.id, (id, previous) => {
    if (id !== previous) clearState();
  }, {flush: "sync"});
  watch(() => resume.file, (file, previous) => {
    if (file === previous) return;
    invalidate();
    resume.uuid = null;
    resume.locationExpectedRevision = null;
    resume.locationExampleEvidence = {};
    locationManuallyEdited.value = false;
  }, {flush: "sync"});
  const contextGuard = () => {
    const generation = parseGeneration;
    const file = resume.file;
    return () => generation === parseGeneration && file === resume.file;
  };
  const updateCandidate = (updated) => {
    const before = locationTuple(candidate);
    const after = locationTuple(updated);
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      locationManuallyEdited.value = true;
      if ((before.city !== after.city || before.state !== after.state) && before.lat === after.lat && before.lng === after.lng) {
        after.lat = after.lng = null;
      }
    }
    Object.assign(candidate, updated, after);
  };

  const reset = () => {
    for (const callback of resetCallbacks.value) {
      if (callback() === true) return;
    }

    clearState();
  };

  /**
   * Uploads the selected resume and stores the identifier it was given.
   *
   * The uploading flag is released even when the upload fails, so a candidate
   * can retry without reloading the page.
   *
   * @param  {ParseResumeApi} api
   * @return {Promise<void>}
   */
  const uploadResume = async (api) => {
    if (resume.uploading) return;
    const generation = parseGeneration;
    const actorId = useAccountStore().user?.id;
    const file = resume.file;
    resume.uploading = true;

    try {
      const formData = serializeFormData({}, "post");
      formData.append("resume", resume.file);

      const response = await api.storeResume(formData);
      if (generation !== parseGeneration || actorId !== useAccountStore().user?.id || file !== resume.file) return;

      resume.uuid = response.uuid;
      resume.locationExpectedRevision = response.location_expected_revision ?? null;
      resume.locationExampleEvidence = response.location_example_evidence ?? {};

      if (!localResume.value) localResume.value = {};
      Object.assign(localResume.value, {
        uuid:          response.uuid,
        locationExpectedRevision: resume.locationExpectedRevision,
        locationExampleEvidence: resume.locationExampleEvidence,
        name:          resume.name,
        formattedSize: resume.formattedSize,
        used:          new Date(),
      });
    } finally {
      if (generation === parseGeneration) resume.uploading = false;
    }
  };

  const setTrustScore = () => {
    let scores = computeCandidateTrustScore(candidate);
    candidate.trustScore = scores.overall;
  };

  const pinLatLng = async (initialLocation, isCurrent) => {
    if (!isCurrent() || locationManuallyEdited.value) return;
    // Parsed text is a suggestion. Only a matching city lookup supplies new coordinates.
    Object.assign(candidate, mergeResumeLocation(initialLocation, {...candidate, lat: null, lng: null}));
    if (validCoordinatePair(candidate) || !candidate.city || !candidate.state) return;
    const requested = locationTuple(candidate);
    const normalize = value => String(value ?? "").trim().toLowerCase();

    try {
      const searchResults = await usaCities({
        search: `${requested.city} ${candidateStateName(requested.state)}`,
        page: 1,
        perpage: 1,
      });

      if (!isCurrent() || locationManuallyEdited.value || candidate.city !== requested.city || candidate.state !== requested.state) return;
      if (searchResults?.data?.length) {
        const result = searchResults.data[0];
        if (normalize(result.city) === normalize(requested.city) && normalize(candidateStateName(result.state)) === normalize(candidateStateName(requested.state)) && validCoordinatePair(result)) {
          candidate.lat = Number(result.lat);
          candidate.lng = Number(result.lng);
        }
      }

    } catch (e) {
      console.error(e);
    }
  };

  const setIndustriesAndInterestedIn = async (api, isCurrent) => {
    try {
      const response = await api.searchIndustriesAndJobTitles(parsedSearchTerms.jobs, parsedSearchTerms.industries);
      if (!isCurrent()) return;
      candidate.interestedIn = response.job_titles || [];
      candidate.industries = response.industries || [];
      const basicInfo = document.getElementById("candidateBasicInfo");
      if (basicInfo) basicInfo.scrollIntoView({behavior: "smooth"});
    } catch (e) {
      console.error(e);
    }
  };

  let thinkingTimout;

  const clearThinkingTimeout = () => {
    resume.thinking = false;
    clearTimeout(thinkingTimout);
  };

  const resetThinkingTimeout = () => {
    clearThinkingTimeout();
    thinkingTimout = setTimeout(() => {
      resume.thinking = !!resume.streaming;
    }, 500);
  };

  const parseResumeContent = async (api, isCurrent) => {

    const eventSource = api.parseResumeContent(
      resume.uuid,
    );
    activeEventSource = eventSource;

    try {
      resume.parsedContent = "";
      resume.streaming = true;

      let handledAtLeastOneEvent = false;
      const initialLocation = locationTuple(candidate);
      let completed = false;

      window.addEventListener("beforeunload", () => eventSource?.close());

      eventSource.addEventListener("update", async function (event) {
        if (!isCurrent() || completed) return;
        if (!handledAtLeastOneEvent) handledAtLeastOneEvent = true;

        if (event.data === "<END_STREAMING_SSE>") {
          completed = true;
          eventSource.close();
          await pinLatLng(initialLocation, isCurrent);
          if (!isCurrent()) return;
          await setIndustriesAndInterestedIn(api, isCurrent);
          if (!isCurrent()) return;
          resume.streaming = false;
          setTrustScore();
          const top = document.getElementById("candidateProfileContainer");
          if (top) top.scrollIntoView({behavior: "smooth", block: "start"});
          return;
        }

        if (!event.data) return;

        resume.parsedContent += event.data;
        let result;

        try {
          result = parsePartialCandidateData(resume.parsedContent, resume.locationExampleEvidence);
          if (result?.searchTerms?.industries) parsedSearchTerms.industries = result.searchTerms.industries;
          if (result?.searchTerms?.jobs) parsedSearchTerms.jobs = result.searchTerms.jobs;
          resetThinkingTimeout();
        } catch (e) {
          console.error(e);
          resume.error = e.message;
          Sentry.captureException(e);
          clearThinkingTimeout();
        }

        if (!result?.data) return;
        const incoming = {...result.data};
        if (locationManuallyEdited.value) {
          [...locationFields, "location"].forEach(key => delete incoming[key]);
        } else {
          // Each parser result is the full current proposal. Rebase on the original answers
          // so a subsequently rejected or retracted field cannot leave a prior fragment behind.
          Object.assign(incoming, mergeResumeLocation(initialLocation, incoming));
        }
        Object.assign(candidate, incoming);
      });

      const handleError = (event, type) => {
        eventSource.close();
        if (!isCurrent()) return;
        resume.streaming = false;

        let errorMsg;
        let toastMsg;

        if (!handledAtLeastOneEvent) {
          errorMsg = "Hmm, it looks like we weren't able to read any data from that resume. Please check that it's not empty, and try again. If the problem persists, please contact support@businessdraft.com";
          toastMsg = "The resume provided is empty. Please upload a resume with content.";
        } else if (type === "transporter-error") {
          errorMsg = "Our resume parsing service is down. Please try again later.";
          toastMsg = "Uh oh! Our third-party resume parsing service is down. Please try again later.";
        } else if (type === "configuration-error") {
          errorMsg = "Our resume parsing service is misconfigured - please contact support.";
          toastMsg = "Our resume parsing service is misconfigured. Please contact support.";
        } else if (type === "unserializable-error") {
          errorMsg = "Our resume parsing service is having trouble reading your resume. Please contact support.";
          toastMsg = " Our resume parsing service is having trouble reading your resume. Please contact support.";
        } else {
          errorMsg = "Lost connection to the server. Please try again.";
          toastMsg = "An error occurred while parsing your resume";
        }

        toast.error(toastMsg);
        resume.error = errorMsg;
      };

      eventSource.addEventListener("transporter-error", (event) => handleError(event, "transporter-error"));
      eventSource.addEventListener("configuration-error", (event) => handleError(event, "configuration-error"));
      eventSource.addEventListener("unserializable-error", (event) => handleError(event, "unserializable-error"));
      eventSource.addEventListener("error", (event) => handleError(event, "error"));

    } catch (e) {
      console.error(e);
      resume.streaming = false;
      clearThinkingTimeout();
      throw new Error("Either 1) The resume was too large (> 6 pages) 2) Our robot detector went off, or 3) The server connection timed-out.");
    }
  };

  const parseResume = async (showTermsOfService = true) => {
    if (resume.loading) return;

    const accountStore = useAccountStore();

    if (showTermsOfService && !resume.tosChecked && !accountStore.isLoggedIn) {
      toast.warning("Please accept the terms of service");
      return;
    }

    if (!resume.file) {
      resume.error = "Please select a resume to upload";
      return;
    }

    const api = new ParseResumeApi();
    const generation = ++parseGeneration;
    const actorId = accountStore.user?.id;
    const isCurrent = () => generation === parseGeneration && actorId === useAccountStore().user?.id;

    resume.loading = true;
    resume.error = null;
    resume.showModal = true;

    try {
      if (!resume.uuid || resume.uuid !== localResume.value?.uuid) await uploadResume(api);
      if (!isCurrent()) return;

      if (accountStore.isLoggedIn) {
        const candidateProfileStore = useCandidateProfileStore();
        Object.assign(candidate, candidateProfileStore.state);
      }

      resume.confirm = true;
      resume.streaming = true;

      await parseResumeContent(api, isCurrent);
      if (!isCurrent()) return;

      resume.streaming = true;
      resume.confirm = true;
    } catch (e) {
      if (!isCurrent()) return;
      console.error(e);
      resume.error = e?.message || "An unknown error occurred while parsing your resume";
    } finally {
      if (isCurrent()) resume.uploading = false;
    }
  };

  /**
   * Parses the contact details out of the uploaded resume.
   *
   * The upload has to have produced an identifier first, otherwise the request
   * is skipped rather than sent with a null uuid for the server to reject.
   *
   * @return {Promise<Object|undefined>}
   */
  const parseContactInfo = async () => {
    if (resume.loading) return;

    const api = new ParseResumeApi();

    const isCurrent = contextGuard();

    if (!resume.uuid || resume.uuid !== localResume.value?.uuid) await uploadResume(api);
    if (!isCurrent()) return;

    if (!resume.uuid) throw new Error("We couldn't upload your resume. Please try again.");

    const result = await api.parseResumeContactInfo(resume.uuid);
    if (isCurrent()) return result;
  };

  const attemptToLoadOldResume = async () => {
    const isCurrent = contextGuard();
    const urlParams = new URLSearchParams(window.location.search);
    const uuidParam = urlParams.get("uuid");
    const usedParam = urlParams.get("used");

    const uuid = localResume.value?.uuid ? localResume.value?.uuid : uuidParam;
    const usedAt = localResume.value?.used ? localResume.value?.used : usedParam;

    if (!uuid || !usedAt) {
      localResume.value = {};
      return;
    }

    if (!localResume.value?.uuid) localResume.value = {uuid, used: usedAt};

    const validForDays = 7;

    if (new Date(usedAt) < new Date(Date.now() - validForDays * 24 * 60 * 60 * 1000)) {
      localResume.value = {};
      return;
    }

    const api = new ParseResumeApi();

    try {
      const response = await api.show(uuid);
      if (!isCurrent()) return;

      const path = response.path;
      if (!path) {
        localResume.value = {};
        return;
      }

      await cloudfrontUrlToFile(path, (error, result) => {
        if (!isCurrent()) return;
        if (error) {
          console.error(error);
          return;
        }

        const name = localResume.value?.name ? localResume.value?.name : "Resume Uploaded";
        const formattedSize = localResume.value?.formattedSize ? localResume.value?.formattedSize : "On another device";

        resume.file = result.file;
        resume.uuid = uuid;
        resume.locationExpectedRevision = response.location_expected_revision ?? localResume.value?.locationExpectedRevision ?? null;
        resume.locationExampleEvidence = response.location_example_evidence ?? {};
        resume.fileDataUrl = result.dataUrl;
        resume.name = name;
        resume.formattedSize = formattedSize;
      });

    } catch (e) {
      console.error(e);
    }
  };

  const completeCallbacks = ref([]);
  const resetCallbacks = ref([]);

  const addCompletionCallback = (callback) => {
    if (typeof callback !== "function") throw new Error("The callback must be a function");
    completeCallbacks.value.push(callback);
  };

  const addResetCallback = (callback) => {
    if (typeof callback !== "function") throw new Error("The callback must be a function");
    resetCallbacks.value.push(callback);
  };

  const flushCompletionCallbacks = () => {
    completeCallbacks.value = [];
    resetCallbacks.value = [];
  };

  const complete = () => {
    completeCallbacks.value.forEach((callback) => callback());
    flushCompletionCallbacks();
  };

  const $reset = () => {
    reset();
  };


  return {
    resume,
    candidate,
    updateCandidate,
    locationManuallyEdited,
    localResume,
    setTrustScore,
    parseResume,
    reset,
    addResetCallback,
    addCompletionCallback,
    flushCompletionCallbacks,
    complete,
    parseContactInfo,
    attemptToLoadOldResume,
    $reset
  };
});
